import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth.ts';
import { ApiError } from '../api/client.ts';
import { useAuthStore } from '../state/authStore.ts';
import { useSeo } from '../hooks/useSeo.ts';

/**
 * Reset-password landing. Reads the token from ?token=…, takes a new password, posts it,
 * gets signed in by the backend (it issues the cookie on a successful reset), and routes
 * the user to home. A missing/blank token short-circuits with a clear error.
 */
export default function ResetPasswordPage() {
  useSeo({ title: 'Choose a new password', canonicalPath: '/reset-password', noindex: true });

  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const setUser = useAuthStore(s => s.setUser);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.resetPassword(token, password),
    onSuccess: async () => {
      const me = await authApi.me();
      setUser(me);
      setDone(true);
      setTimeout(() => navigate('/'), 1200);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) setFormError(err.detail || err.title);
      else setFormError('Something went wrong. Please try again.');
    },
  });

  useEffect(() => {
    if (!token) setFormError('No token in the link. Open the email and click the button again.');
  }, [token]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!token) return;
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }
    reset.mutate({ token, password });
  };

  if (done) {
    return (
      <div className="sign-in">
        <h1>Password updated</h1>
        <p className="muted">You're signed in. Taking you to the scanner…</p>
        <Link to="/" className="cta">Run a scan</Link>
      </div>
    );
  }

  return (
    <div className="sign-in">
      <h1>Choose a new password</h1>
      <p className="muted">Pick something you can remember. At least 8 characters.</p>

      <form className="auth-form" onSubmit={onSubmit}>
        <label>
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={128}
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </label>
        <label>
          <span>Confirm new password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={128}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
          />
        </label>

        {formError && <p className="error" role="alert">{formError}</p>}

        <button type="submit" className="cta" disabled={reset.isPending || !token}>
          {reset.isPending ? 'Updating…' : 'Update password'}
        </button>
      </form>

      <p className="muted small auth-toggle">
        <Link to="/forgot-password">Need a fresh reset link?</Link>
      </p>
    </div>
  );
}
